import { Stack, Switch, Typography } from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import { useState } from "react";
import { useFragment } from "react-relay";
import { UniversalLinkCard_brand$key } from "./__generated__/UniversalLinkCard_brand.graphql";
import CardSection from "../../../../components/CardSection";
import LeftRight from "../../../../components/LeftRight";
import SaveButton, { SavedState } from "../../../../components/SaveButton";
import useBrandAppConfigUpdateMutation from "../../../../mutations/useBrandAppConfigUpdateMutation";

const brandFragment = graphql`
  fragment UniversalLinkCard_brand on BrandType {
    appConfig {
      id
      iosUniversalLinksEnabled
      androidAppLinksEnabled
    }
  }
`;

type Props = {
  brand: UniversalLinkCard_brand$key;
};

const UniversalLinkCard = ({ brand: brandKey }: Props) => {
  const brand = useFragment<UniversalLinkCard_brand$key>(
    brandFragment,
    brandKey
  );
  const appConfig = brand.appConfig;

  const [saveButtonState, setSaveButtonState] = useState<SavedState>(
    SavedState.SAVED
  );
  const [isIOSEnabled, setIsIOSEnabled] = useState<boolean>(
    appConfig.iosUniversalLinksEnabled
  );
  const [isAndroidEnabled, setIsAndroidEnabled] = useState<boolean>(
    appConfig.androidAppLinksEnabled
  );

  const handleIOSChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsIOSEnabled(e.target.checked);
    if (e.target.checked !== appConfig.iosUniversalLinksEnabled) {
      setSaveButtonState(SavedState.ENABLED);
    } else if (isAndroidEnabled === appConfig.androidAppLinksEnabled) {
      setSaveButtonState(SavedState.SAVED);
    }
  };

  const handleAndroidChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsAndroidEnabled(e.target.checked);
    if (e.target.checked !== appConfig.androidAppLinksEnabled) {
      setSaveButtonState(SavedState.ENABLED);
    } else if (isIOSEnabled === appConfig.iosUniversalLinksEnabled) {
      setSaveButtonState(SavedState.SAVED);
    }
  };

  const [saveMutation, isMutationInFlight] = useBrandAppConfigUpdateMutation(
    {
      id: appConfig.id,
      iosUniversalLinksEnabled: isIOSEnabled,
      androidAppLinksEnabled: isAndroidEnabled,
    },
    () => {
      setSaveButtonState(SavedState.SAVED);
    }
  );

  return (
    <CardSection
      actions={
        <SaveButton
          savedState={
            isMutationInFlight ? SavedState.DISABLED : saveButtonState
          }
          onClick={saveMutation}
        />
      }
      title="Universal Links"
      subtitle="Enable Universal Links"
      showIsOptional
      content={
        <Stack spacing={2} sx={{ maxWidth: "650px" }}>
          <LeftRight
            expandLeft={true}
            left={
              <Stack spacing={1}>
                <Typography variant="subtitle1">
                  Enable iOS Universal Links
                </Typography>
                <Typography variant="body2">
                  Create a seamless shopping experience by allowing iOS
                  customers to open your app directly when clicking Shopify
                  store links in emails or messages
                </Typography>
              </Stack>
            }
            right={<Switch checked={isIOSEnabled} onChange={handleIOSChange} />}
          />
          <LeftRight
            expandLeft={true}
            left={
              <Stack spacing={1}>
                <Typography variant="subtitle1">
                  Enable Android App Links
                </Typography>
                <Typography variant="body2">
                  Create a seamless shopping experience by allowing Android
                  customers to open your app directly when clicking Shopify
                  store links in emails or messages
                </Typography>
              </Stack>
            }
            right={
              <Switch
                checked={isAndroidEnabled}
                onChange={handleAndroidChange}
              />
            }
          />
        </Stack>
      }
    />
  );
};

export default UniversalLinkCard;
